import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Task, CreateTaskInput, UpdateTaskInput, Documento } from '../types';

export const taskKeys = {
  all: ['tasks'] as const,
  list: (projectId: string) => [...taskKeys.all, projectId] as const,
};

export function useTasks(projectId: string | null | undefined) {
  return useQuery({
    queryKey: taskKeys.list(projectId ?? ''),
    queryFn: () => api.get<Task[]>(`/tasks?projectId=${projectId}`),
    enabled: !!projectId,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTaskInput) => api.post<Task>('/tasks', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.list(variables.projectId) });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; projectId: string; data: UpdateTaskInput }) =>
      api.patch<Task>(`/tasks/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.list(variables.projectId) });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; projectId: string }) => api.delete(`/tasks/${id}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.list(variables.projectId) });
    },
  });
}

/**
 * Riordina i task: `ids` è la lista nell'ordine voluto e l'indice diventa il campo `ordine`.
 * L'aggiornamento è ottimistico, così la riga trascinata resta dove l'hai lasciata.
 */
export function useRiordinaTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, ids }: { projectId: string; ids: string[] }) =>
      api.patch<Task[]>('/tasks/riordina', { projectId, ids }),
    onMutate: async ({ projectId, ids }) => {
      const key = taskKeys.list(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const precedenti = queryClient.getQueryData<Task[]>(key);

      if (precedenti) {
        const perId = new Map(precedenti.map((t) => [t.id, t]));
        const spostati = ids.map((id) => perId.get(id)).filter((t): t is Task => !!t);
        // I task fuori dalla lista riordinata (es. le completate) restano in coda come sono
        const resto = precedenti.filter((t) => !ids.includes(t.id));
        queryClient.setQueryData(key, [...spostati, ...resto]);
      }

      return { precedenti };
    },
    onError: (_err, variables, context) => {
      if (context?.precedenti) {
        queryClient.setQueryData(taskKeys.list(variables.projectId), context.precedenti);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.list(variables.projectId) });
    },
  });
}

/** Allega un file a un task: passa dall'endpoint documenti con taskId valorizzato. */
export function useAllegaFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      projectId: string;
      taskId: string;
      nome: string;
      file: { data: string; mediaType: string; filename: string };
    }) =>
      api.post<Documento>('/documenti', {
        projectId: payload.projectId,
        taskId: payload.taskId,
        nome: payload.nome,
        categoria: 'cantiere',
        file: payload.file,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.list(variables.projectId) });
    },
  });
}
